import React, { useState } from 'react'
import { Card, Button, Table, Upload, message, Modal, Progress } from 'antd'
import { UploadOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons'
import { VITE_API_BASE as API_BASE } from '../config/apiConfig'

const { Dragger } = Upload

const BatchImport = () => {
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [results, setResults] = useState(null)
  const [templateType, setTemplateType] = useState('parents') // parents | children | products

  // 下载模板
  const downloadTemplate = () => {
    let content = ''
    let filename = ''

    if (templateType === 'parents') {
      filename = '家长导入模板.csv'
      content = `phone,nickname,region\n13800138000,张三,北京\n13800138001,李四,上海`
    } else if (templateType === 'children') {
      filename = '孩子导入模板.csv'
      content = `parent_phone,name,gender,birthday\n13800138000,张小明,男,2018-05-01\n13800138001,李小红,女,2019-03-15`
    } else if (templateType === 'products') {
      filename = '产品导入模板.csv'
      content = `name,category,price,stock,description\n识字课程,课程,169,100,趣味学汉字\n阅读课程,课程,169,100,经典故事阅读`
    } else if (templateType === 'orders') {
      filename = '订单导入模板.csv'
      content = `phone,product_name,quantity,total_amount\n13800138000,识字课程,1,169\n13800138001,阅读课程,1,169`
    }

    const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    link.click()
    URL.revokeObjectURL(link.href)
    message.success('模板下载成功')
  }

  // 上传并导入
  const handleUpload = async (file) => {
    if (!file) return false

    setImporting(true)
    setProgress(0)
    setResults(null)

    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', templateType)

    try {
      // 模拟导入进度
      const interval = setInterval(() => {
        setProgress(p => {
          if (p >= 90) {
            clearInterval(interval)
            return 90
          }
          return p + 10
        })
      }, 200)

      const response = await fetch(`${API_BASE}/admin/batch-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      })

      clearInterval(interval)
      setProgress(100)

      const data = await response.json()
      
      if (data.success) {
        setResults({
          total: data.data.total,
          success: data.data.success,
          failed: data.data.failed,
          errors: data.data.errors || []
        })
        message.success(`导入完成：成功 ${data.data.success} 条，失败 ${data.data.failed} 条`)
      } else {
        message.error(data.error || '导入失败')
      }

    } catch (err) {
      message.error('导入失败: ' + err.message)
    }

    setImporting(false)
    return false // 阻止默认上传
  }

  // 导入结果表格
  const renderResults = () => {
    if (!results) return null

    const columns = [
      { title: '行号', dataIndex: 'row', width: 80 },
      { title: '手机号/名称', dataIndex: 'value', width: 150 },
      { title: '错误原因', dataIndex: 'error' }
    ]

    return (
      <Card title="导入结果" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 24, color: '#52c41a', fontWeight: 'bold' }}>{results.success}</div>
            <div style={{ color: '#666' }}>成功</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 24, color: '#f5222d', fontWeight: 'bold' }}>{results.failed}</div>
            <div style={{ color: '#666' }}>失败</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 24, color: '#667eea', fontWeight: 'bold' }}>{results.total}</div>
            <div style={{ color: '#666' }}>总计</div>
          </div>
        </div>

        {results.errors.length > 0 && (
          <>
            <div style={{ fontWeight: 'bold', marginBottom: 8 }}>失败详情：</div>
            <Table
              columns={columns}
              dataSource={results.errors}
              rowKey="row"
              size="small"
              pagination={false}
            />
          </>
        )}
      </Card>
    )
  }

  return (
    <div style={{ padding: 24 }}>
      <h2>📥 批量导入</h2>

      {/* 导入类型选择 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontWeight: 'bold', marginRight: 16 }}>导入类型：</span>
          {[
            { id: 'parents', name: '家长用户' },
            { id: 'children', name: '孩子档案' },
            { id: 'products', name: '产品' },
            { id: 'orders', name: '订单' }
          ].map(type => (
            <Button
              key={type.id}
              type={templateType === type.id ? 'primary' : 'default'}
              onClick={() => setTemplateType(type.id)}
              style={{ marginRight: 8 }}
            >
              {type.name}
            </Button>
          ))}
        </div>

        <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
          下载{templateType === 'parents' ? '家长' : templateType === 'children' ? '孩子' : templateType === 'products' ? '产品' : '订单'}导入模板
        </Button>
      </Card>

      {/* 上传区域 */}
      <Card>
        <Dragger
          name="file"
          multiple={false}
          accept=".csv,.xlsx,.xls"
          beforeUpload={handleUpload}
          disabled={importing}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: 48, color: '#667eea' }} />
          </p>
          <p className="ant-upload-text">点击或拖拽上传文件</p>
          <p className="ant-upload-hint">
            支持 CSV、Excel 格式，建议文件不超过1000条数据
          </p>
        </Dragger>

        {importing && (
          <div style={{ marginTop: 16 }}>
            <Progress percent={progress} status="active" />
            <div style={{ textAlign: 'center', color: '#666' }}>导入中，请稍候...</div>
          </div>
        )}
      </Card>

      {/* 导入结果 */}
      {renderResults()}

      {/* 导入说明 */}
      <Card title="📋 导入说明" style={{ marginTop: 16 }}>
        <ul style={{ lineHeight: 2 }}>
          <li>请先下载对应类型的导入模板</li>
          <li>按照模板格式填写数据，不要修改表头</li>
          <li>手机号必须为11位数字，不能重复</li>
          <li>日期格式：YYYY-MM-DD（如：2018-05-01）</li>
          <li>建议单次导入不超过1000条数据</li>
          <li>导入过程中请不要关闭页面</li>
        </ul>
      </Card>
    </div>
  )
}

export default BatchImport
